import SharedConfig from '@packages/systems/core/constants/SharedConfig';
import _ from 'lodash';

/**
 * Contains JsonDom utils shared by server and client
 */

/**
 * Checks to see if a node is an anchor.
 * @deprecated Use isWebflowLink() instead.
 * @param  {String}  tagName The name of the tag, in lowercase.
 * @return {Boolean}         True if this node is a link.
 */
export const isLink = function (tagName: string): boolean {
  return tagName === 'a';
};

/**
 * Checks to see if a node is a link block (not just a link)
 * @param  {String}  tagName
 * @param  {Object}  data    Node Data
 * @return {Boolean}         True if node is a link block
 */
export const isLinkBlock = function (tagName: string, data: any): boolean {
  return isLink(tagName) && Boolean(data) && data.block === 'inline';
};

/**
 * Whether the node is a page link
 * @param  {Object}  data Node data
 * @return {Boolean}      True if data represents a phone link
 */
export const isLinkPage = function (data: any): boolean {
  return Boolean(data && data.link && data.link.mode === 'page');
};

/**
 * Detects whether this node has Webflow link data.
 * @param  {Object}  data Node metadata.
 * @return {Boolean}
 */
export const isWebflowLink = function (data: any): boolean {
  return Boolean(data && data.link);
};

/**
 * Checks to see if this is a button node.
 * @param  {Object}  data Node metadata.
 * @return {Boolean}
 */
export const isButton = function (data: any): boolean {
  return Boolean(data && data.button === true);
};

/**
 * Check to see if a node is part of an ordered or unordered list.
 * @param  {Object}  data  The node's metadata.
 * @return {Boolean}
 */
export const isListEl = function (data: any): boolean {
  return Boolean(data && data.list);
};

/**
 * Checks to see if a node is a ordered/unordered list.
 * @param  {Object } data   The node's metadata.
 * @return {Boolean}        True if this is a Webflow-created list.
 */
export const isList = function (data: any): boolean {
  return isListEl(data) && data.list.type === 'list';
};

/**
 * Check to see if a node is a list item
 * @param  {Object}  data  The node's metadata.
 * @return {Boolean}       True if node is list item
 */
export const isListItem = function (data: any): boolean {
  return isListEl(data) && data.list.type === 'item';
};

/**
 * Indicates whether the tag is a heading.
 * @param  {String}  tagName  The name of the tag, in lowercase.
 * @return {Boolean}          True if this is a tag that is a H1-H6 tag.
 */
export const isBlockquote = function (tagName: string): boolean {
  return tagName === 'blockquote';
};

/**
 * Indicates whether the tag is a heading.
 * @param  {String}  tagName  The name of the tag, in lowercase.
 * @return {Boolean}          True if this is a tag that is a H1-H6 tag.
 */
export const isHeading = function (tagName: string): boolean {
  return /^(h[1-6])$/i.test(tagName);
};

/**
 * Checks to see if a node is an inline image.
 * @param  {String}  tagName
 * @return {Boolean}         True if the node is an image
 */
export const isImage = function (tagName: string): boolean {
  return tagName === 'img';
};

/**
 * Checks to see if a node is a paragraph.
 * @param  {String}  tagName
 * @return {Boolean}
 */
export const isParagraph = function (tagName: string): boolean {
  return tagName === 'p';
};

/**
 * Indicates whether this is a core grid node (container, row, column).
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if this data represents a grid node.
 */
export const isGridEl = function (data: any): boolean {
  return Boolean(data && data.grid && data.grid.type);
};

/**
 * Indicates whether this is a layout section.
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if this data represents a grid section.
 */
export const isGridSection = function (data: any): boolean {
  return isGridEl(data) && data.grid.type === 'section';
};

/**
 * Indicates whether this is a grid row.
 * @param  {Object}  data Node data.
 * @return {Boolean}
 */
export const isGridRow = function (data: any): boolean {
  return isGridEl(data) && data.grid.type === 'row';
};

/**
 * Whether the element is a column section
 * @param  {Object}  data Node data
 * @return {Boolean}      True if grid column
 */
export const isGridColumn = function (data: any): boolean {
  return isGridEl(data) && data.grid.type === 'col';
};

/**
 * Whether the element is a grid container
 * @param  {Object}  data Node data
 * @return {Boolean}      True if grid container
 */
export const isGridContainer = function (data: any): boolean {
  return isGridEl(data) && data.grid.type === 'container';
};

/**
 * Indicates whether a node belongs to a form.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if this node represents a form element.
 */
export const isFormEl = function (data: any): boolean {
  return Boolean(data && data.form);
};

/**
 * Indicates whether a node is a form form
 * @param  {Object}  data Node data
 * @return {Boolean}
 */
export const isFormForm = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'form';
};

/**
 * Indicates whether a node is a form wrapper
 * @param  {Object}  data Node data
 * @return {Boolean}
 */
export const __DEPRECATED__isFormWrapper = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'wrapper';
};

/**
 * Indicates whether a node is a form file upload label
 * @param  {Object}  data Node data
 * @return {Boolean}
 */
export const isFormFileUploadLabel = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'file-upload-label';
};

/**
 * Indicates whether a node is a form file uploading button
 * @param  {Object}  data Node data
 * @return {Boolean}
 */
export const isFormFileUploadUploadingBtn = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'file-upload-uploading-btn';
};

/**
 * Indicates whether a form element lives directly inside the <div class="w-form"> wrapper.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormWrapperChild = function (data: any): boolean {
  return isFormForm(data) || isFormMessage(data);
};

/**
 * Indicates whether this is a form node that applies broadly to the entire form.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormSettingsNode = function (data: any): boolean {
  return (
    __DEPRECATED__isFormWrapper(data) ||
    isFormWrapperChild(data) ||
    isFormButton(data)
  );
};

/**
 * Indicates whether a node belongs to a password page form.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if this node represents a required form element for a password page.
 */
export const isPasswordPageFormEl = function (data: any): boolean {
  return isFormEl(data) && data.form.passwordPage === true;
};

/**
 * Indicates whether this is a password page form node that applies broadly to the entire form.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isPasswordPageFormSettingsNode = function (data: any): boolean {
  return isPasswordPageFormEl(data);
};

/**
 * Indicates whether this is a pure <label> element that connects to a non-radio, non-checkbox input.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormBlockLabel = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'label';
};

/**
 * Indicates whether this is a checkbox or radio inline label (situated right next to the input instead of above).
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormInlineLabel = function (data: any): boolean {
  return isFormCheckboxLabel(data) || isFormRadioLabel(data);
};

/**
 * Indicates whether a node is a form label that can point to an input field.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormLabel = function (data: any): boolean {
  return isFormBlockLabel(data) || isFormInlineLabel(data);
};

/**
 * Indicates whether a node is a form select.
 * @param  {Object}  data  Node data, either from DomNode or from JSON.
 * @return {Boolean}       True if this is a form select list.
 */
export const isFormSelect = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'select';
};

/**
 * Indicates whether this node is the wrapper node around a <checkbox> + <label> combination.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormCheckboxWrapper = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'checkbox';
};

/**
 * Indicates whether this node is the actual <input type="checkbox"> inside a form.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormCheckboxInput = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'checkbox-input';
};

/**
 * Indicates whether this node is the inline label for a checkbox input.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormCheckboxLabel = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'checkbox-label';
};

/**
 * Indicates whether this node is the wrapper node around a <radio> + <label> combination.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormRadioWrapper = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'radio';
};

/**
 * Indicates whether this node is the actual <input type="radio"> inside a form.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormRadioInput = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'radio-input';
};

/**
 * Indicates whether this node is the inline label for a checkbox input.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormRadioLabel = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'radio-label';
};

/**
 * Indicates whether this is a <textarea> node.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormBasicInput = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'input';
};

/**
 * Indicates whether this is a <textarea> node.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormTextarea = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'textarea';
};

/**
 * Indicates whether this a textual form input, like <input type="text"> or <textarea>.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormTextInput = function (data: any): boolean {
  return isFormBasicInput(data) || isFormTextarea(data);
};

/**
 * Indicates whether a node is a form input that can have a form label attached to it.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}         True if it's a <input> or <select> or <textarea> element.
 */
export const isFormInput = function (data: any): boolean {
  return (
    isFormSelect(data) ||
    isFormTextInput(data) ||
    isFormCheckboxInput(data) ||
    isFormRadioInput(data)
  );
};

/**
 * Indicates whether a node is a wrapper around an input and label.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}         True if it's a <div> that contains a checkbox or radio button + label.
 */
export const isFormInputWrapper = function (data: any): boolean {
  return isFormCheckboxWrapper(data) || isFormRadioWrapper(data);
};

/**
 * Indicates whether a node is a form input button.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormButton = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'button';
};

/**
 * Indicates whether a node is the success / done div that shows after submit.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormSuccessMessage = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'msg-done';
};

/**
 * Indicates whether a node is the error / fail div that shows if the AJAX request fails.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormErrorMessage = function (data: any): boolean {
  return isFormEl(data) && data.form.type === 'msg-fail';
};

/**
 * Indicates whether a node is a form message outside of the <form> node.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isFormMessage = function (data: any): boolean {
  return isFormSuccessMessage(data) || isFormErrorMessage(data);
};

/**
 * Indicates whether a node is a wrapper around embedded HTML.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if the data represents an embed (e.g. video, iframe, etc) node.
 */
export const isEmbed = function (data: any): boolean {
  return Boolean(data && data.embed);
};

/**
 * Indicates whether a node is a search input.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if it the node is a search input.
 */
export const isSearchInput = function (data: any): boolean {
  return Boolean(data && data.search && data.search.type === 'input');
};

/**
 * Indicates whether a node is a search form wrapper.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if it the node is a search form wrapper.
 */
export const isSearchForm = function (data: any): boolean {
  return Boolean(data && data.search && data.search.type === 'form');
};

/**
 * Indicates whether a node is a search button.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if it the node is a search button.
 */
export const isSearchButton = function (data: any): boolean {
  return Boolean(data && data.search && data.search.type === 'button');
};

/**
 * Indicates whether a node is a search result list.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if it the node is a search form wrapper.
 */
export const isSearchResultWrapper = function (data: any): boolean {
  return Boolean(data && data.search && data.search.type === 'wrapper');
};

export const isSearchResultList = function (data: any): boolean {
  return Boolean(data && data.search && data.search.type === 'list');
};

export const isSearchResultItem = function (data: any): boolean {
  return Boolean(data && data.search && data.search.type === 'item');
};

export const isSearchResultEmptyMessage = function (data: any): boolean {
  return Boolean(data && data.search && data.search.type === 'empty');
};

/**
 * Indicates whether a node is a wrapper around dynamic embedded HTML.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if the data represents a dynamic embed (e.g. video, iframe, etc) node.
 */
export const isCompilableHtmlEmbed = function (data: any): boolean {
  return (
    isHtmlEmbed(data) && Boolean(data.embed.meta && data.embed.meta.compilable)
  );
};

/**
 * Whether the node is a video embed
 * @param  {Object}  data Node data
 * @return {Boolean}      True if data represents a video embed
 */
export const isVideo = function (data: any): boolean {
  return isEmbed(data) && data.embed.type === 'video';
};

/**
 * Indicates whether this node is a iframe or script embed.
 * @param  {Object}  data The node data.
 * @return {Boolean}
 */
export const isHtmlEmbed = function (data: any): boolean {
  return isEmbed(data) && data.embed.type === 'html';
};

/**
 * Indicates whether this node is a non-dynamic iframe or script embed.
 * @param  {Object}  data The node data.
 * @return {Boolean}
 */
export const isStaticHtmlEmbed = function (data: any): boolean {
  return isHtmlEmbed(data) && !isCompilableHtmlEmbed(data);
};

/**
 * Indicates whether a node is a widget.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if the data represents a widget (e.g. maps, etc) node.
 */
export const isWidget = function (data: any): boolean {
  return Boolean(data && data.widget);
};

export const isMapWidget = function (data: any): boolean {
  return isWidget(data) && data.widget.type === 'map';
};

export const isFacebookWidget = function (data: any): boolean {
  return isWidget(data) && data.widget.type === 'facebook';
};

export const isTwitterWidget = function (data: any): boolean {
  return isWidget(data) && data.widget.type === 'twitter';
};

export const isGPlusWidget = function (data: any): boolean {
  return isWidget(data) && data.widget.type === 'gplus';
};

export const isIconWidget = function (data: any): boolean {
  return isWidget(data) && data.widget.type === 'icon';
};

// Detects any element with slider-namespace metadata
export const isSliderEl = function (data: any): boolean {
  return Boolean(data && data.slider);
};

/**
 * Indicates whether this is a slider wrapper node.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isSliderWrapper = function (data: any): boolean {
  return isSliderEl(data) && data.slider.type === 'wrapper';
};

/**
 * Detects any immediate slider wrapper child, each of which is reserved and can't take children.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isSliderWrapperChild = function (data: any): boolean {
  return isSliderMask(data) || isSliderArrow(data) || isSliderNav(data);
};

/**
 * Detects the reserved slider mask node that wraps all slides.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isSliderMask = function (data: any): boolean {
  return isSliderEl(data) && data.slider.type === 'slides';
};

/**
 * Detects an individual slide element, which can take children.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isSliderSlide = function (data: any): boolean {
  return isSliderEl(data) && data.slider.type === 'slide';
};

/**
 * Detects whether this is a slider left/right arrow.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isSliderArrow = function (data: any): boolean {
  return isSliderEl(data) && data.slider.type === 'arrow';
};

/**
 * Indicates whether this is the slider nav icon holder.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isSliderNav = function (data: any): boolean {
  return isSliderEl(data) && data.slider.type === 'nav';
};

/**
 * Indicates whether this is a slider element that can't take direct children.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isSliderClosedEl = function (data: any): boolean {
  return isSliderWrapper(data) || isSliderWrapperChild(data);
};

/**
 * Indicates whether a node is the outer lightbox trigger wrapper.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isLightboxWrapper = function (data: any): boolean {
  return Boolean(data && data.lightbox) && data.lightbox.type === 'wrapper';
};

/**
 * Indicates whether a node is part of a navbar.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isNavbarEl = function (data: any): boolean {
  return Boolean(data && data.navbar);
};

/**
 * Indicates whether a node is the outer navbar wrapper.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isNavbarWrapper = function (data: any): boolean {
  return isNavbarEl(data) && data.navbar.type === 'wrapper';
};

/**
 * Indicates whether a node is the inner container directly inside a navbar.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isNavbarContainer = function (data: any): boolean {
  return isNavbarEl(data) && data.navbar.type === 'container';
};

/**
 * Indicates whether a node is the brand link wrapper.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isNavbarBrand = function (data: any): boolean {
  return isNavbarEl(data) && data.navbar.type === 'brand';
};

/**
 * Indicates whether a node is the hamburger link that opens the menu.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isNavbarButton = function (data: any): boolean {
  return isNavbarEl(data) && data.navbar.type === 'button';
};

/**
 * Indicates whether a node is the navbar link holder.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isNavbarMenu = function (data: any): boolean {
  return isNavbarEl(data) && data.navbar.type === 'menu';
};

/**
 * Indicates whether a nav item link inside the menu.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isNavbarLink = function (data: any): boolean {
  return isNavbarEl(data) && data.navbar.type === 'link';
};
/**
 * Indicates whether a node is any kind of dropdown element
 * @param  {Object}  data Node data
 * @return {Boolean}
 */
export const isDropdownEl = function (data: any): boolean {
  return Boolean(data && data.dropdown);
};

/**
 * Indicates whether a node is the outer dropdown wrapper.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if node is a dropdown wrapper.
 */
export const isDropdownWrapper = function (data: any): boolean {
  return isDropdownEl(data) && data.dropdown.type === 'wrapper';
};

/**
 * Indicates whether a node is a dropdown toggle
 * @param  {Object}  data Node data
 * @return {Boolean}
 */
export const isDropdownToggle = function (data: any): boolean {
  return isDropdownEl(data) && data.dropdown.type === 'toggle';
};

/**
 * Indicates whether a node is a dropdown list
 * @param  {Object}  data Node data
 * @return {Boolean}
 */
export const isDropdownList = function (data: any): boolean {
  return isDropdownEl(data) && data.dropdown.type === 'list';
};

/**
 * Indicates whether a node is a dropdown link
 * @param  {Object}  data Node data
 * @return {Boolean}
 */
export const isDropdownLink = function (data: any): boolean {
  return isDropdownEl(data) && data.dropdown.type === 'link';
};

/**
 * Indicates whether a node is part of a tabs component.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isTabsEl = function (data: any): boolean {
  return Boolean(data && data.tabs);
};

/**
 * Indicates whether a node is the outer tabs wrapper.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isTabsWrapper = function (data: any): boolean {
  return isTabsEl(data) && data.tabs.type === 'wrapper';
};

/**
 * Indicates whether this is a tabs link holder.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isTabsMenu = function (data: any): boolean {
  return isTabsEl(data) && data.tabs.type === 'menu';
};

/**
 * Indicates whether this is a tab link.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isTabsLink = function (data: any): boolean {
  return isTabsEl(data) && data.tabs.type === 'link';
};

/**
 * Indicates whether this is a tab pane wrapper.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isTabsContent = function (data: any): boolean {
  return isTabsEl(data) && data.tabs.type === 'content';
};

/**
 * Indicates whether this is an individual tab pane.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isTabsPane = function (data: any): boolean {
  return isTabsEl(data) && data.tabs.type === 'pane';
};

/**
 * Indicates whether a node belongs to a tabs link/pane pair.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isTabsPair = function (data: any): boolean {
  return isTabsLink(data) || isTabsPane(data);
};

/**
 * Indicates whether a node is an immediate child of the tabs wrapper.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}
 */
export const isTabsWrapperChild = function (data: any): boolean {
  return isTabsMenu(data) || isTabsContent(data);
};

/**
 * Indicates whether a node is a background video element.
 * @param  {Object}  data Node metadata.
 * @return {Boolean}
 */
export const isBackgroundVideoEl = function (data: any): boolean {
  return Boolean(data && data.bgvideo);
};

/**
 * Indicates whether a node is the background video outermost wrapper element.
 * @param  {Object}  data Node metadata.
 * @return {Boolean}
 */
export const isBackgroundVideoWrapper = function (data: any): boolean {
  return isBackgroundVideoEl(data) && data.bgvideo.type === 'wrapper';
};

/**
 * Indicates whether a DOM node is a special type of wrapper around other nodes.
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if the node is a component (widget, slider, navbar, embed).
 */
export const isComponent = function (data: any): boolean {
  return (
    isEmbed(data) ||
    isWidget(data) ||
    isSliderClosedEl(data) ||
    isNavbarWrapper(data) ||
    isDropdownWrapper(data) ||
    isTabsWrapper(data)
  );
};

/**
 * Indicates whether a node is typographic.
 * @param  {String}  tagName  The name of the tag, in lowercase.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if this is a tag that can only contain text.
 */
export const isTypographic = function (tagName: string, data: any): boolean {
  data = data || {};

  if (isNavbarBrand(data)) {
    return false;
  }

  if (isFormFileUploadLabel(data)) {
    return false;
  }

  // Some tags are always typographic
  if (SharedConfig.TYPOGRAPHIC_TAG_REGEX.test(tagName)) {
    return true;
  }

  // Anchor tags can sometimes be block nodes (e.g. for Button Block elements)
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 2.
  if (isLink(tagName, data)) {
    // Navbar brand elements are block
    const navbarType = data.navbar && data.navbar.type;
    if (navbarType === 'brand') {
      return false;
    }

    const block = data.block === true || data.block === 'inline';
    return !block;
  }

  // Div/LI blocks can sometimes be typographic (e.g. for Text Block elements)
  if (isTypographicBlock(tagName, data)) {
    return true;
  }

  return false;
};

/**
 * Determines if the tag name is allowed to be typographic.
 * @param  {String}   tagName The name of the tag.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if this is a block node that has direct inner text.
 */
export const isTypographicBlock = function (
  tagName: string,
  data: any
): boolean {
  return (
    isAllowedTypographicTag(tagName) && Boolean(data && data.text === true)
  );
};

/**
 * Determines if this a super basic Div Block node.
 * @param  {String}  tagName The name of the tag.
 * @param  {Object}  data    The node's data.
 * @return {Boolean}         True if this is a vanilla non-text, non-rich Div Block.
 */
export const isDivBlock = function (tagName: string, data: any): boolean {
  return tagName === 'div' && (!data || _.isEqual(data, {}));
};

/**
 * Determines if the tag represents an HTML5 semantic containing element.
 * @param  {String}  tagName The name of the tag.
 * @param  {Object}  data    Node data
 * @return {Boolean}         True if it's a div-like tag that's allowed to be changed to another tag name.
 */
export const isBlock = function (tagName: string, data: any): boolean {
  data = data || {};
  const validTag =
    /^(div|main|article|aside|blockquote|figure|footer|header|menu|nav|section|address)$/.test(
      tagName
    );

  return (
    validTag &&
    !isTypographic(tagName, data) &&
    !isFormEl(data) &&
    !isComponent(data) &&
    !isSymbolInst(data)
  );
};

/**
 * Determines if a node is specifically marked as inline (used by Link Block).
 * @param  {Object}  data Node metadata.
 * @return {Boolean}
 */
export const isInlineBlock = function (data: any): boolean {
  return Boolean(data && data.block === 'inline');
};

/**
 * Determines if a node is specifically marked as block.
 * @param  {Object}  data Node metadata.
 * @return {Boolean}
 */
export const isBlockForced = function (data: any): boolean {
  return Boolean(data && data.block === true);
};

export const isBlockExplicit = function (data: any): boolean {
  return isInlineBlock(data) || isBlockForced(data);
};

/**
 * Determine if an element is allowed to be a rich text block. Cash money??
 * @param  {String}  tagName The tag name of the node.
 * @param  {Object}  data  Node metadata.
 * @return {Boolean}         True if this is a block node that has rich text inside.
 */
export const isRichBlock = function (tagName: string, data: any): boolean {
  return Boolean(
    data &&
    data.rich === true &&
    isBlock(tagName, data) &&
    !isTypographicBlock(tagName, data)
  );
};

/**
 * Indicates whether a node is allowed to store inner HTML (instead of DomNode children).
 * @param  {String}  tagName The tag name of the node.
 * @param  {Object}  data    The node's data.
 * @return {Boolean}         True if this is a typographic node or a rich text node.
 */
export const isTextHolder = function (tagName: string, data: any): boolean {
  if (isLightboxWrapper(data)) {
    return false;
  }
  return isTypographic(tagName, data) || isRichBlock(tagName, data);
};

/**
 * Detects whether a node represents the actual symbol root node.
 * This can happen when we're editing a symbol and it's been put in place of one of the instances.
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if this node represents a root symbol node.
 */
export const isSymbolRoot = function (data: any): boolean {
  return Boolean(data && data.sym && data.sym.root === true);
};

/**
 * Detects whether a node is an instance of a symbol.
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if this node represents another symbol.
 */
export const isSymbolInst = function (data: any): boolean {
  return Boolean(data && data.sym && data.sym.inst);
};

/**
 * Detects whether a node is an instance of a symbol or a symbol root.
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if this node represents a symbol.
 */
export const isSymbol = function (data: any): boolean {
  return Boolean(data && (isSymbolInst(data) || isSymbolRoot(data)));
};

/**
 * Detects whether a node represents the symbol root node of a library component.
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if this node represents a library root symbol node.
 */
export const isLibrarySymbolRoot = function (data: any): boolean {
  return isSymbolRoot(data) && Boolean(data && data.sym && data.sym.origin);
};

/**
 * Detects whether the node is part of a dynamic list.
 * @param  {Object}  data Node metadata.
 * @return {Boolean}
 */
export const isDynamoPluginEl = function (data: any): boolean {
  return Boolean(data && data.dyn && data.dyn.type);
};

/**
 * Detects whether the current element is the dynlist wrapper.
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if this node represents the wrapper that nothing can be dragged into.
 */
export const isDynamoWrapper = function (data: any): boolean {
  return isDynamoPluginEl(data) && data.dyn.type === 'wrapper';
};

/**
 * Detects whether the current element is the dynlist items wrapper.
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if this node represents the list wrapper.
 */
export const isDynamoList = function (data: any): boolean {
  return isDynamoPluginEl(data) && data.dyn.type === 'list';
};

/**
 * Detects whether a node is an instance of a dyn list item or a dyn list root item template.
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if this node represents a dyn list item.
 */
export const isDynamoItem = function (data: any): boolean {
  return isDynamoPluginEl(data) && data.dyn.type === 'item';
};

/**
 * Detects whether this is a dynamic list's empty message wrapper.
 * @param  {Object}  data Node data.
 * @return {Boolean}      True if this node represents a transient/non-persisted dyn list item.
 */
export const isDynamoEmpty = function (data: any): boolean {
  return isDynamoPluginEl(data) && data.dyn.type === 'empty';
};

/**
 * Detects whether a node's value is bound to a dynamic path.
 * @param  {Object}  data     Node data.
 * @param  {String}  property (Optional) If specified, will only return true if the specific property (e.g. 'innerHTML', 'src', etc) is bound.
 * @return {Boolean}          True if this node contains dynamic bindings.
 */
export const isDynBound = function (data: any, property?: string): boolean {
  const hasBindings = Boolean(
    data && data.dyn && data.dyn.bind && Object.keys(data.dyn.bind).length
  );

  if (hasBindings && property) {
    return Boolean(data.dyn.bind[property]);
  }

  return hasBindings;
};

/**
 * Detects whether a node's innerHTML value is bound to a dynamic path.
 * @param  {Object}  data     Node data.
 * @return {Boolean}
 */
export const isDynBoundHtml = function (data: any): boolean {
  return isDynBound(data, 'innerHTML');
};

/**
 * Detects whether a node's has a dynamic condition set.
 * @param  {Object}  data     Node data.
 * @return {Boolean}          True if this node contains a dynamic conditon.
 */
export const isDynConditioned = function (data: any): boolean {
  return Boolean(
    data &&
    data.dyn &&
    data.dyn.condition &&
    Object.keys(data.dyn.condition).length
  );
};

/**
 * Detects whether a node is dynamically bound or has a dynamic condition set.
 * @param  {Object}  data     Node data.
 * @return {Boolean}          True if this node is dynamically bound or conditioned.
 */
export const isDynBoundOrConditioned = function (data: any): boolean {
  return isDynBound(data) || isDynConditioned(data);
};

/**
 * Detects whether a node is an unbound dynlist (no collection)
 * @param  {Object}  data     Node data.
 * @return {Boolean}          True if this node is unbound and a list item.
 */
export const isUnboundDynamoWrapper = function (data: any): boolean {
  return isDynamoWrapper(data) && !data.dyn.collection;
};

/**
 * Indicates whether a node is a standard node that can accept children.
 * @param  {String}  tagName  The name of the tag, in lowercase.
 * @param  {Object}  data     Node data, either from DomNode or from JSON.
 * @return {Boolean}          True if this a plain vanilla node.
 */
export const isStandard = function (tagName: string, data: any): boolean {
  if (SharedConfig.VOID_HTML_TAGS.has(tagName)) {
    return false;
  }

  if (isTextHolder(tagName, data)) {
    return false;
  }
  if (isFormSelect(data)) {
    return false;
  }
  if (isFormTextarea(data)) {
    return false;
  }

  // Symbol instances are not editable, elements must be added to the symbol root instead
  if (isSymbolInst(data)) {
    return false;
  }

  // Certain holder elements have managed children nodes
  if (isDynamoWrapper(data)) {
    return false;
  }
  if (isDynamoList(data)) {
    return false;
  }

  // Embeds and widgets (maps, FB, twitter, etc) can never take children
  if (isEmbed(data)) {
    return false;
  }
  if (isWidget(data)) {
    return false;
  }

  // Slider nav wrappers currently can't take children (they are auto-generated)
  if (isSliderNav(data)) {
    return false;
  }

  return true;
};

/**
 * Determines if a node has a custom attribute defined in the JSON data.
 * @param  {Object}  data     The node's data.
 * @param  {String}  attrName The name of the attribute.
 * @return {Boolean}          True if the custom attribute is specified.
 */
export const hasCustomAttr = function (data: any, attrName: string): boolean {
  return (
    Boolean(data) &&
    Boolean(data.xattr) &&
    // @ts-expect-error - TS7006 - Parameter 'attr' implicitly has an 'any' type.
    data.xattr.some((attr) => attr.name === attrName)
  );
};

/**
 * Checks if a tag name is allowed to become typographic.
 * @param  {String}  tagName The name of a tag.
 * @return {Boolean}         True if the node is allowed to be typographic.
 */
export const isAllowedTypographicTag = function (tagName: string): boolean {
  return tagName === 'div' || tagName === 'li' || tagName === 'blockquote';
};
